import Clickable from 'components/Common/Clickable'
import Caption from 'LuxKit/Typography/Caption'
import { useAppSelector } from 'hooks/reduxHooks'
import withMountGuard from 'hocs/withMountGuard'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import { rem } from 'polished'
import type { MouseEventHandler, ReactNode } from 'react'
import { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import useLoyaltyActiveChooseDoubleRewards from '../hooks/useLoyaltyActiveChooseDoubleRewards'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import type { TakeoverOptions } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { SOCIETE_CHOOSE_DOUBLE_REWARDS_TAKEOVER_ID } from '../loyaltyChooseDoubleRewardsTakeover.constants'
import useLoyaltyOpenChooseDoubleRewardsFlow from '../hooks/useLoyaltyOpenChooseDoubleRewardsFlow'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useLoyaltyChooseDoubleRewardsRegion from '../hooks/useLoyaltyChooseDoubleRewardsRegion'
import useLoyaltyChooseDoubleRewardsTracking from '../hooks/useLoyaltyChooseDoubleRewardsTracking'
import { LabFeatureFlags } from 'constants/lab'
import { matchesLoyaltyDoubleRewardsOfferCriteria } from '../utils/loyaltyChooseDoubleRewardsMetadata'

const Pill = styled(Clickable)`
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  padding: ${rem(4)} ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.round};
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

function renderUnderline(chunks: ReactNode) {
  return <u>{chunks}</u>
}

type Props =
  | {
    offer: NonNullable<TakeoverOptions['offer']>
    offerType?: never
    campaignTags?: never
  }
  | {
    offer?: never
    offerType: App.OfferType
    campaignTags: Array<string>
  }

function LoyaltyChooseDoubleRewardsOfferPill({ offer, offerType, campaignTags }: Props) {
  const { impressionRef, track } = useLoyaltyChooseDoubleRewardsTracking('offer_pill')
  const takeover = useCurrentSiteTakeover({ offer })
  const metadataFlag = useAppSelector(state => state.system.features.labFlags[LabFeatureFlags.enableSiteTakeoverMetadata])
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const openFlow = useLoyaltyOpenChooseDoubleRewardsFlow()
  const loggedIn = useAppSelector(selectLoggedIn)
  const hasAccount = useAppSelector(state => !!state.loyalty.account.data)
  const { needsRegionSwitch, currency } = useLoyaltyChooseDoubleRewardsRegion()

  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    track('clicked')
    openFlow()
  }, [openFlow, track])

  // Use custom check for PropertyRates which isn't structured as an offer.
  // mount guard already handles generic site takeover eligibility check
  if (takeover?.id !== SOCIETE_CHOOSE_DOUBLE_REWARDS_TAKEOVER_ID ||
    (offerType && !matchesLoyaltyDoubleRewardsOfferCriteria({ type: offerType, campaignTags }, metadataFlag))) return null

  return <Pill ref={impressionRef} className={`tier-${tier}`} onClick={handleClick}>
    <Caption variant="medium" weight="bold">
      {(!loggedIn || !hasAccount || !needsRegionSwitch) && <FormattedMessage
        id="JnNaVoISiVzisXI7VkYa"
        defaultMessage="Double {programName} Rewards · <underline>Activate</underline>"
        description="Offer card pill inviting an unenrolled member to activate double rewards"
        values={{ underline: renderUnderline, programName: getLoyaltyProgramDisplayName() }}
      />}
      {loggedIn && hasAccount && needsRegionSwitch && currency && <FormattedMessage
        id="b7ALJkWKOP_7gLQ7VQoq"
        defaultMessage="Double {programName} Rewards · <underline>Switch to {currency}</underline>"
        description="Offer card pill inviting a member to switch to their loyalty currency for double rewards"
        values={{ underline: renderUnderline, programName: getLoyaltyProgramDisplayName(), currency }}
      />}
      {loggedIn && hasAccount && needsRegionSwitch && !currency && <FormattedMessage
        id="HAGVv7tpTLlA5BOGG5Fq"
        defaultMessage="Double {programName} Rewards · <underline>Switch region</underline>"
        description="Offer card pill inviting a member to switch to their loyalty region for double rewards"
        values={{ underline: renderUnderline, programName: getLoyaltyProgramDisplayName() }}
      />}
    </Caption>
  </Pill>
}

export default withMountGuard(() => {
  const rewards = useLoyaltyActiveChooseDoubleRewards()
  return !!rewards && rewards.memberState.status !== 'enrolled' && !!rewards.endsAt
})(LoyaltyChooseDoubleRewardsOfferPill)
